import { CircleCheck, XCircle } from "lucide-react";
import { type FC, useCallback } from "react";
import { Link } from "react-router-dom";

import { type OptionItem } from "@/api/admin";
import {
  type TopUpInfo,
  useApproveTopUpMutation,
  useRejectTopUpMutation,
} from "@/api/admin/diners";
import {
  type AdminTopUpsReportingResponse,
  type ITopUp,
  STATUS_TOPUPS_VARIANT_MAP,
  TOP_UPS_STATUS,
  topUpsStatusMap,
} from "@/api/admin/reports";
import { AdminSections } from "@/api/admin/teamRoles";
import { QUERY_KEYS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";
import {
  Loader,
  TableCellBadge,
  TableCellLink,
  TableCellText,
  TableHeadCellCustom,
  TableHeadCellSort,
  type TableHeaderType,
} from "@/components/atoms";
import {
  BasicModal,
  DropDownViewMenu,
  FilterSearch,
  type SubMenuConfig,
  toastError,
  toastSuccess,
} from "@/components/molecules";
import type { SubMenuItem } from "@/components/molecules/FilterSearch/DropDownFilterMenu";
import { PATHS } from "@/config/path";
import ApproveActionForm from "@/modules/AdminDiners/ApproveActionForm";
import { usePermission } from "@/services/casl/usePermission";
import { commonIconProps14 } from "@/utils/constants";
import { DEFAULT_NAME } from "@/utils/constants/defaultNames";
import { createSortHandler } from "@/utils/createSortHandler";
import { formatEnumValue } from "@/utils/formatEnumValue";
import { formatIsoDate } from "@/utils/formatIsoToUTC";
import { useAdminMoney } from "@/utils/hooks/useAdminMoney";
import { type IGroupParams } from "@/utils/hooks/useTableParams";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";
import { parseDateRangeToParams } from "@/utils/parseDateRangeToParams";

import DropDownMenu from "../DropDownMenu";
import DynamicTable from "../DynamicTable";
import { type ColumnConfig } from "../type";

interface ApproveTopUpModalProps {
  row: ITopUp;
  isPending: boolean;
  onConfirm: (approvedAmount: number) => void;
}

const ApproveTopUpModal: FC<ApproveTopUpModalProps> = ({
  row,
  isPending,
  onConfirm,
}) => {
  return (
    <BasicModal
      title="Approve Top-up"
      body={
        <ApproveActionForm
          topUp={row as TopUpInfo}
          isPending={isPending}
          onConfirm={onConfirm}
        />
      }
      trigger={
        <div className="flex items-center gap-2 cursor-pointer px-2 py-1.5 w-full rounded-[6px] focus:bg-accent hover:bg-accent focus:text-accent-foreground text-sm">
          <CircleCheck {...commonIconProps14} color="#65A30D" />
          <span>Approve</span>
        </div>
      }
      trigerClasses="w-full"
      btnTitle="Confirm"
      footer={false}
    />
  );
};

enum TableColumn {
  CREATED_AT = "Created at",
  APPROVED_AT = "Approved at",
  DINER_NAME = "Diner Name",
  REQUESTED_AMOUNT = "Requested amount",
  APPROVED_AMOUNT = "Approved amount",
  REASON = "Reason",
  STATUS = "Status",
  ACTIONS = "",
}

const TABLE_HEADERS: TableHeaderType = Object.values(TableColumn).filter(
  header => header !== ""
);

const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
  [TableColumn.APPROVED_AT]: { type: "date" },
  [TableColumn.REASON]: {
    type: "search_input",
    placeholder: "Search by reason",
  },
  [TableColumn.STATUS]: { type: "status" },
};

interface TableProps {
  tableData?: AdminTopUpsReportingResponse;
  tableParams: IGroupParams;
  setTableParams: React.Dispatch<React.SetStateAction<IGroupParams>>;
  handleNext: () => void;
  handlePrev: () => void;
  searchValue: string;
  setSearchValue: (val: string) => void;
  isPending: boolean;
}

const AdminTopUpsReportingTable: FC<TableProps> = ({
  tableData,
  setTableParams,
  handleNext,
  handlePrev,
  searchValue,
  setSearchValue,
  isPending,
  tableParams,
}) => {
  const hasTopUpUpdatePermission = usePermission(AdminSections.TOP_UPS, {
    isUpdate: true,
  });
  const hasDinerUpdatePermission = usePermission(AdminSections.DINER, {
    isUpdate: true,
  });
  const hasDinerDetailsPermission = usePermission(AdminSections.DINER);

  const SUB_MENUS: Record<string, SubMenuItem[]> = {
    [TableColumn.APPROVED_AT]: [],
    [TableColumn.STATUS]: Object.keys(topUpsStatusMap),
    [TableColumn.REASON]: [],
  };

  const MENU_LIST = Object.keys(SUB_MENUS);

  const handleSetFilter = (
    selectedMap: Record<string, string[] | OptionItem[]>
  ) => {
    const selectedStatuses =
      (selectedMap[TableColumn.STATUS] as string[] | undefined) ?? [];

    const rawStatuses = selectedStatuses
      .map(status => topUpsStatusMap[status])
      .filter(Boolean);

    const { dateFrom: approvedFrom, dateTo: approvedTo } =
      parseDateRangeToParams(selectedMap[TableColumn.APPROVED_AT]?.[0], true);

    const reason = selectedMap[TableColumn.REASON]?.[0] as string | undefined;

    setTableParams(prev => ({
      ...prev,
      status: rawStatuses.length ? rawStatuses : undefined,
      approvedTo,
      approvedFrom,
      reason,
      offset: 0,
    }));
  };

  const { visibleColumns, setVisibleColumns, flags } =
    useVisibleFlags(TABLE_HEADERS);

  const handleSortCreatedAt = createSortHandler(setTableParams, "createdAt");
  const handleSortApprovedAt = createSortHandler(setTableParams, "processedAt");
  const handleSortRequestedAmount = createSortHandler(
    setTableParams,
    "requestedAmount"
  );
  const handleSortApprovedAmount = createSortHandler(
    setTableParams,
    "approvedAmount"
  );

  const { mutate: approveTopUp, isPending: isPendingApprove } =
    useApproveTopUpMutation();

  const { mutate: rejectTopUp, isPending: isPendingReject } =
    useRejectTopUpMutation();

  const handleAction = useCallback(
    (
      id: string,
      type: "approve" | "reject",
      mutationFn: typeof approveTopUp | typeof rejectTopUp,
      extraData?: { approvedAmount: number }
    ) => {
      const payload =
        type === "approve"
          ? { topUpRequestId: id, approvedAmount: extraData?.approvedAmount }
          : { topUpRequestId: id };

      mutationFn(payload, {
        onSuccess: () => {
          toastSuccess(`Top-up was ${type}ed successfully!`);
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.topUpsReporting],
          });
        },
        onError: error => {
          toastError(
            error.message || "Oops! Something went wrong.",
            () => handleAction(id, type, mutationFn),
            "Error"
          );
        },
      });
    },
    []
  );

  const getActions = (row: ITopUp) => {
    if (row.status !== TOP_UPS_STATUS.PENDING) return [];

    return [
      {
        dialog: (
          <ApproveTopUpModal
            row={row}
            isPending={isPendingApprove}
            onConfirm={amount =>
              handleAction(row.id, "approve", approveTopUp, {
                approvedAmount: amount,
              })
            }
          />
        ),
      },
      {
        dialog: (
          <BasicModal
            title="Reject Top-up"
            btnTitle="Confirm"
            isPending={isPendingReject}
            action={() => handleAction(row.id, "reject", rejectTopUp)}
            body={
              <p className="text-sm text-gray-950 mb-2">
                Are you sure you want to reject this top-up?
              </p>
            }
            trigger={
              <div className="flex items-center gap-2 cursor-pointer px-2 py-1.5 w-full rounded-[6px] focus:bg-accent hover:bg-accent focus:text-accent-foreground text-sm">
                <XCircle {...commonIconProps14} color="#DC2626" />
                <span>Reject</span>
              </div>
            }
            trigerClasses="w-full"
          />
        ),
      },
    ];
  };

  const actionColumn: ColumnConfig<ITopUp> | null =
    hasTopUpUpdatePermission && hasDinerUpdatePermission
      ? {
          key: "actions",
          label: TableColumn.ACTIONS,
          visible: true,
          headerComponent: ({ label }) => (
            <TableHeadCellCustom>{label}</TableHeadCellCustom>
          ),
          cellRenderer: (row, isLast) => {
            const rowActions = getActions(row);
            if (rowActions.length === 0)
              return <TableCellText last={isLast}> </TableCellText>;

            return (
              <TableCellText classes="w-[4%]" last={isLast}>
                <DropDownMenu menuList={rowActions} />
              </TableCellText>
            );
          },
        }
      : null;

  const money = useAdminMoney();

  const columns: ColumnConfig<ITopUp>[] = [
    {
      key: "createdAt",
      label: TableColumn.CREATED_AT,
      visible: flags[TableColumn.CREATED_AT],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortCreatedAt}
          field="createdAt"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatIsoDate(row?.createdAt) || "-"}
        </TableCellText>
      ),
    },
    {
      key: "processedAt",
      label: TableColumn.APPROVED_AT,
      visible: flags[TableColumn.APPROVED_AT],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortApprovedAt}
          field="processedAt"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row.status === TOP_UPS_STATUS.APPROVED
            ? formatIsoDate(row?.processedAt)
            : "-"}
        </TableCellText>
      ),
    },
    {
      key: "name",
      label: TableColumn.DINER_NAME,
      visible: flags[TableColumn.DINER_NAME],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => {
        const name = row?.name ?? DEFAULT_NAME;

        return hasDinerDetailsPermission ? (
          <TableCellLink last={isLast}>
            <Link
              to={PATHS.ADMIN.DINERS_USER(row.dinerId)}
              state={{ from: location.pathname }}
              data-testid={`cell-id-${row.name}`}
            >
              {name}
            </Link>
          </TableCellLink>
        ) : (
          <TableCellText last={isLast}>{name}</TableCellText>
        );
      },
    },
    {
      key: "requestedAmount",
      label: TableColumn.REQUESTED_AMOUNT,
      visible: flags[TableColumn.REQUESTED_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortRequestedAmount}
          field="requestedAmount"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.requestedAmount?.toString())}
        </TableCellText>
      ),
    },
    {
      key: "approvedAmount",
      label: TableColumn.APPROVED_AMOUNT,
      visible: flags[TableColumn.APPROVED_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortApprovedAmount}
          field="approvedAmount"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row.status === TOP_UPS_STATUS.APPROVED
            ? money.render(row?.approvedAmount?.toString())
            : "-"}
        </TableCellText>
      ),
    },
    {
      key: "reason",
      label: TableColumn.REASON,
      visible: flags[TableColumn.REASON],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>{row?.reason || "-"}</TableCellText>
      ),
    },
    {
      key: "status",
      label: TableColumn.STATUS,
      visible: flags[TableColumn.STATUS],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => {
        if (!row?.status) {
          return <TableCellText last={isLast}> - </TableCellText>;
        }
        return (
          <TableCellBadge
            last={isLast}
            classes="text-center"
            stage={
              STATUS_TOPUPS_VARIANT_MAP[formatEnumValue(row.status)]?.badge
            }
          >
            {formatEnumValue(row?.status)}
          </TableCellBadge>
        );
      },
    },
    ...(actionColumn ? [actionColumn] : []),
  ];

  return (
    <>
      <FilterSearch
        menuList={MENU_LIST}
        subMenus={SUB_MENUS}
        subMenuRegistry={SUB_MENU_REGISTRY}
        statusVariant={STATUS_TOPUPS_VARIANT_MAP}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSetFilter={handleSetFilter}
      >
        <DropDownViewMenu
          viewList={TABLE_HEADERS}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />
      </FilterSearch>

      {isPending ? (
        <Loader />
      ) : (
        <DynamicTable<ITopUp>
          data={tableData?.items || []}
          columns={columns}
          searchValue={searchValue}
          next={handleNext}
          prev={handlePrev}
          tableParams={tableData}
          setTableParams={setTableParams}
        />
      )}
    </>
  );
};

export default AdminTopUpsReportingTable;
